import { Component, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { apiErrorMessage, JjamesButtonComponent } from 'shared-lib';
import { ParkingSpaceService } from '../../../api/gen';
import { ParkingSpaceDto } from '../../../api/gen/model/parkingSpaceDto';

export interface EasypassModesDialogData {
    parkingSpace: ParkingSpaceDto;
}

/**
 * Switches the Easypass modes of one space on or off — features JJames enables per customer, so the
 * caller shows this to Administrators only (the endpoint refuses everyone else). Always sends all
 * three switches: the endpoint replaces them, like the Quick Easypass one.
 */
@Component({
    selector: 'app-easypass-modes-dialog',
    standalone: true,
    imports: [MatDialogModule, MatIconModule, JjamesButtonComponent, TranslatePipe],
    templateUrl: './easypass-modes-dialog.component.html'
})
export class EasypassModesDialogComponent {
    private dialogRef = inject(MatDialogRef<EasypassModesDialogComponent>);
    private parkingSpaceService = inject(ParkingSpaceService);
    private snackBar = inject(MatSnackBar);
    private translate = inject(TranslateService);
    data = inject<EasypassModesDialogData>(MAT_DIALOG_DATA);

    simple = signal(this.data.parkingSpace.easypassSimpleEnabled ?? false);
    multiQr = signal(this.data.parkingSpace.easypassMultiQrEnabled ?? false);
    advanced = signal(this.data.parkingSpace.easypassAdvancedEnabled ?? false);
    isSaving = signal(false);

    readonly rows = [
        { key: 'easypassModes.simple', value: this.simple },
        { key: 'easypassModes.multiQr', value: this.multiQr },
        { key: 'easypassModes.advanced', value: this.advanced }
    ];

    onCancel(): void {
        this.dialogRef.close(false);
    }

    onSave(): void {
        if (this.isSaving()) return;

        this.isSaving.set(true);
        this.parkingSpaceService
            .parkingSpaceParkingSpaceIdEasypassModesPut(this.data.parkingSpace.id!, {
                simpleEnabled: this.simple(),
                multiQrEnabled: this.multiQr(),
                advancedEnabled: this.advanced()
            })
            .subscribe({
                next: () => {
                    this.isSaving.set(false);
                    this.dialogRef.close(true);
                },
                error: (err) => {
                    this.isSaving.set(false);
                    this.snackBar.open(
                        apiErrorMessage(err, this.translate.instant('easypassModes.saveFailed')), 'Dismiss', { duration: 5000 });
                }
            });
    }
}
